import { getLoadDateLabel } from "@/lib/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Truck, 
  MapPin, 
  Route, 
  Clock, 
  ChevronRight,
  PackageOpen,
  AlertCircle,
} from "lucide-react";
import { PickupsMap } from "@/components/loads/PickupsMap";
import { PickupCard, PickupData } from "@/components/loads/PickupCard";
import { isToday } from "date-fns";
import { 
  LOAD_STATUS_LABELS, 
  LOAD_STATUS_COLORS,
  LoadStatus,
} from "@/config/constants";
import { DEPOT_LOCATION } from "@/config/locations";
import { InstallationWithRelations } from "@/types/installations";

interface Load {
  id: string;
  load_date: string;
  status: string;
  total_distance_km?: number | null;
  estimated_duration_min?: number | null;
  notes?: string | null;
  installer_id?: string | null;
  optimized_order?: unknown;
  route_geometry?: unknown;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

interface MyLoadsListViewProps {
  loads: Load[];
  installationsMap: Record<string, InstallationWithRelations[]>;
  unassignedPickups: PickupData[];
  claimedPickups: PickupData[];
  isLoading: boolean;
  onSelectLoad: (loadId: string) => void;
  onClaimPickup: (pickupId: string) => void;
  onCompletePickup: (installationId: string, orderId: string) => void;
  onSendSms: (installationId: string, orderId: string) => void;
  isClaimingPickup: boolean;
  sendingSmsFor: string | null;
  notifiedInstallations: Set<string>;
  isCompleteLoading: boolean;
  onShowFailedActions?: () => void;
  failedActionsCount?: number;
}


export function MyLoadsListView({
  loads,
  installationsMap,
  unassignedPickups,
  claimedPickups,
  isLoading,
  onSelectLoad,
  onClaimPickup,
  onCompletePickup,
  onSendSms,
  isClaimingPickup,
  sendingSmsFor,
  notifiedInstallations,
  isCompleteLoading,
  onShowFailedActions,
  failedActionsCount = 0,
}: MyLoadsListViewProps) {
  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Omat kuormat</h1>
          <p className="text-muted-foreground">Päivän reitit ja tehtävät</p>
        </div>
        {failedActionsCount > 0 && onShowFailedActions && (
          <Button
            variant="destructive"
            size="sm"
            onClick={onShowFailedActions}
            className="gap-2"
          >
            <AlertCircle className="h-4 w-4" />
            {failedActionsCount} epäonnistunut
          </Button>
        )}
      </div>

      {/* Unassigned pickups section */}
      {unassignedPickups && unassignedPickups.length > 0 && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-700 dark:text-red-400">
              <PackageOpen className="h-5 w-5" />
              Noudettavia lähellä ({unassignedPickups.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Map */}
            <div className="h-48 rounded-lg overflow-hidden border">
              <PickupsMap
                pickups={unassignedPickups
                  .filter((pickup) => {
                    const lat = pickup.latitude || pickup.cemetery?.latitude;
                    const lng = pickup.longitude || pickup.cemetery?.longitude;
                    return lat && lng;
                  })
                  .map((pickup) => ({
                    id: pickup.id,
                    lat: Number(pickup.latitude || pickup.cemetery?.latitude),
                    lng: Number(pickup.longitude || pickup.cemetery?.longitude),
                    orderNumber: pickup.order?.order_number || "",
                    cemeteryName: pickup.cemetery?.name || "",
                    cemeteryCity: pickup.cemetery?.city || "",
                  }))
                }
                center={DEPOT_LOCATION}
                onClaimPickup={(id) => onClaimPickup(id)}
                isClaimPending={isClaimingPickup}
              />
            </div>

            {/* Pickup list */}
            <div className="space-y-2">
              {unassignedPickups.slice(0, 3).map((pickup) => (
                <PickupCard
                  key={pickup.id}
                  pickup={pickup}
                  variant="compact"
                  showClaimButton={true}
                  onClaim={() => onClaimPickup(pickup.id)}
                  isClaimLoading={isClaimingPickup}
                />
              ))}
              {unassignedPickups.length > 3 && (
                <div className="text-center text-xs text-muted-foreground">
                  + {unassignedPickups.length - 3} muuta
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Claimed pickups section */}
      {claimedPickups && claimedPickups.length > 0 && (
        <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-orange-700 dark:text-orange-400">
              <PackageOpen className="h-5 w-5" />
              Omat noudot ({claimedPickups.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {claimedPickups.map((pickup) => (
              <PickupCard
                key={pickup.id}
                pickup={pickup}
                variant="full"
                showCompleteButton={true}
                onComplete={() => pickup.order?.id && onCompletePickup(pickup.id, pickup.order.id)}
                onSendSms={() => pickup.order?.id && onSendSms(pickup.id, pickup.order.id)}
                isSmsLoading={sendingSmsFor === pickup.id}
                isNotified={notifiedInstallations.has(pickup.id)}
                isCompleteLoading={isCompleteLoading}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Loading / Empty state */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Ladataan...</div>
      ) : !loads || loads.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Ei kuormia</h3>
            <p className="text-muted-foreground mt-1">
              Sinulle ei ole vielä määritetty kuormia.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {loads.map((load) => {
            const installations = installationsMap?.[load.id] || [];
            const completedCount = installations.filter(i => i.status === "completed").length;
            const isLoadToday = isToday(new Date(load.load_date));
            const isLoadActive = load.status === "in_progress";

            return (
              <Card 
                key={load.id}
                className={`cursor-pointer transition-all hover:shadow-md ${isLoadToday || isLoadActive ? "ring-2 ring-primary" : ""}`}
                onClick={() => onSelectLoad(load.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center
                        ${isLoadToday ? "bg-primary text-primary-foreground" : "bg-muted"}
                      `}>
                        <Truck className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="font-medium">
                          {getLoadDateLabel(load.load_date)}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {installations.length} kohdetta
                          </span>
                          {load.total_distance_km && (
                            <span className="flex items-center gap-1">
                              <Route className="h-3 w-3" />
                              {Math.round(load.total_distance_km)} km
                            </span>
                          )}
                          {load.estimated_duration_min && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {Math.floor(load.estimated_duration_min / 60)}h
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <Badge className={LOAD_STATUS_COLORS[load.status as LoadStatus]}>
                          {LOAD_STATUS_LABELS[load.status as LoadStatus]}
                        </Badge>
                        {installations.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {completedCount}/{installations.length} valmis
                          </div>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
